import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useGoalsStore } from "@/apps/goals/store/goalsStore";
import { formatGoalRewardAmount } from "@/apps/goals/utils/formatTime";
import Button from "@/ui/Button/Button";
import Modal from "@/ui/Modal/Modal";
import {
  GoalMultiplierSpinWheel,
} from "./GoalMultiplierSpinWheel";
import cls from "./styles.module.scss";

interface GoalMultiplierValueModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: number;
  goalMultiplierValues: number[];
}

const formatMultiplier = (value: number) => `x${value}`;

export const GoalMultiplierValueModal = ({
  isOpen,
  onClose,
  value,
  goalMultiplierValues
}: GoalMultiplierValueModalProps) => {
  const { t } = useTranslation();
  const rewardAmount = useGoalsStore(
    (state) => state.initData?.program.goal_price ?? 0
  );
  const resultRewardAmount = rewardAmount * value;
  const sectorAngle = 360 / goalMultiplierValues.length;
  const selectedSector = Math.max(
    goalMultiplierValues.findIndex((multiplier) => multiplier === value),
    0
  );
  const sectorMidAngle = selectedSector * sectorAngle + sectorAngle / 2;
  const rotation = (360 - sectorMidAngle) % 360;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideHeader
      wrapperClassName={cls.valueModalWrapper}
      wrapperClosingClassName={cls.valueModalWrapperClosing}
      contentClassName={cls.valueModalContent}
      closeAnimationMs={320}
    >
      <div className={cls.valueSheet}>
        <h2 className={cls.resultTitle}>
          <Trans
            i18nKey="goals.multiplier.resultTitle"
            values={{ multiplier: formatMultiplier(value) }}
            components={{ accent: <span className={cls.resultTitleAccent} /> }}
          />
        </h2>
        <p className={cls.resultSubtitle}>
          {t("goals.multiplier.resultSubtitle")}
        </p>

        <div className={cls.valueWheelCrop}>
          <GoalMultiplierSpinWheel
            rotation={rotation}
            spinning={false}
            selectedMultiplier={value}
            multipliers={goalMultiplierValues}
          />
        </div>

        <div className={[cls.rewardExample, cls.valueReward].join(" ")}>
          <div className={cls.rewardExampleRow}>
            <span className={cls.rewardCurrent}>
              {formatGoalRewardAmount(rewardAmount)}
            </span>
            <span className={cls.rewardMultiplier}>
              {formatMultiplier(value)}
            </span>
            <svg className={cls.rewardArrow} viewBox="0 0 34 16" aria-hidden>
              <path d="M2 8H30M23 2L30 8L23 14" />
            </svg>
            <strong className={cls.rewardResult}>
              {formatGoalRewardAmount(resultRewardAmount)}
            </strong>
          </div>
        </div>

        <Button
          className={cls.backButton}
          label={t("goals.multiplier.backToGoal")}
          onClick={onClose}
        />
      </div>
    </Modal>
  );
};
